import React, { useEffect, useState } from "react";
import HeadingSample from "../components/utility/headingSample";
import { motion } from "framer-motion";
import { FaSearch } from 'react-icons/fa';
import TextPanel from "../components/Home/section3/textPanel";
import { db } from "../components/firebase/firebase";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { differenceInCalendarDays, differenceInCalendarWeeks, differenceInCalendarMonths } from "date-fns";
import { useNavigate } from "react-router-dom";

const sec1Subtitle = "Get to know information";
const sec1Title = ["Our blogs", "#F8D835"];

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  timestamp: Date;
  image: string;
}

const categories = ['Daily Reports', 'Weekly Reports', 'Monthly Reports'];
const Blogs: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>([]);
  const [selectedCategory, setSelectedCategory] = useState(categories[0]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 9;
  const navigate = useNavigate();
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const q = query(collection(db, "blogs"), orderBy("createdAt", "desc"));
        const snapshot = await getDocs(q);
        const blogs: BlogPost[] = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            title: data.title || "Untitled",
            excerpt: data.content || "",
            timestamp: data.createdAt?.toDate() || new Date(),
            image: data.image || "/default-image.jpg",
          };
        });
        setPosts(blogs);
        setFilteredPosts(blogs);
      } catch (error) {
        console.error("Error fetching blog posts:", error);
      }
    };

    fetchPosts();
  }, []);

  useEffect(() => {
    let updated = [...posts];
    const now = new Date();

    // Category-based filtering
    updated = updated.filter((post) => {
      if (selectedCategory === "Daily Reports") {
        return differenceInCalendarDays(now, post.timestamp) <= 1;
      }
      if (selectedCategory === "Weekly Reports") {
        return differenceInCalendarWeeks(now, post.timestamp) <= 1;
      }
      if (selectedCategory === "Monthly Reports") {
        return differenceInCalendarMonths(now, post.timestamp) <= 1;
      }
      return true;
    });

    // Search filtering
    if (searchTerm) {
      updated = updated.filter((post) =>
        post.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredPosts(updated);
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, posts]);

  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const currentPosts = filteredPosts.slice((currentPage - 1) * postsPerPage, currentPage * postsPerPage);

  const topArticle = posts[0];
  const topImage = topArticle?.image || "/blogs/featured.svg";
  const top_title = "";
  const top_subtitle = topArticle?.title || "Default subtitle";
  const top_contexts = [topArticle?.excerpt || "Default content"];

  return (
    <div className="w-full flex flex-col items-center justify-center bg-white box-border overflow-hidden">
      {/* Hero Section */}
      <motion.div
        id="blog_home"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="h-[500px] sm:h-[700px] w-full bg-[url('../public/blogs/section1.svg')] bg-black/30 bg-blend-overlay bg-cover bg-center"
      >
        <HeadingSample subtitle={sec1Subtitle} title={sec1Title} />
      </motion.div>

      {/* Main Content */}
      <div className="w-full flex flex-col justify-center items-center bg-white px-4 sm:px-6 md:px-10 lg:px-12">
        <motion.div
          className="w-full flex flex-col lg:flex-row justify-center items-center gap-6 sm:gap-10 lg:gap-12 py-8"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="w-full max-w-7xl mx-auto p-4">
            {/* Filters */}
            <div id="latest_Articles" className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 mb-6 w-full">
              <div className="hidden md:flex flex-wrap gap-2 sm:gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-4 py-2 rounded-lg text-xs lg:text-sm z-40 ${selectedCategory === cat
                      ? 'bg-blue-800 text-white'
                      : 'bg-[#F9F9F9] text-gray-800'
                      }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              {/* Dropdown - Mobile */}
              <div className="block z-40 md:hidden">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-4 py-2 border rounded-lg w-full"
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              {/* Search Bar */}
              <div className="relative w-full sm:w-auto sm:ml-auto z-40">
                <input
                  type="text"
                  placeholder="Search any news"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full sm:w-64 px-4 py-2 pl-10 border rounded-lg"
                />
                <FaSearch className="absolute left-3 top-3 text-gray-400" />
              </div>
            </div>

            {/* Featured Block */}
            <motion.div
              className="w-full flex flex-col lg:flex-row justify-center items-start lg:gap-12 py-8"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="w-full max-w-md mx-auto">
                <img src={topImage} alt="topImage" className="w-full h-auto rounded-[36px]" />
              </div>
              <TextPanel title={top_title} subtitle={top_subtitle} contexts={top_contexts} />
            </motion.div>

            {/* Blog Cards Grid */}
            <div id="update" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentPosts.map((post) => (
                <div
                  key={post.id}
                  onClick={() => navigate(`/blog/${post.id}`)}
                  className="bg-white shadow rounded-lg overflow-hidden cursor-pointer hover:shadow-md transition"
                >
                  <img src={post.image} alt={post.title} className="h-48 w-full object-cover" />
                  <div className="p-4">
                    <h3 className="text-lg font-bold mt-1">{post.title}</h3>
                    <p className="text-sm text-gray-600 mt-2">
                      {post.excerpt?.slice(0, 100) + "..."}
                    </p>
                    <p className="text-xs text-blue-600 font-semibold float-right mb-1">
                      {post.timestamp.toLocaleDateString("en-US", {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="flex justify-center items-center gap-2 mt-10 flex-wrap">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="w-8 h-8 rounded-md border hover:bg-gray-100 text-sm disabled:opacity-50"
              >
                ←
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((page) =>
                  page === 1 ||
                  page === totalPages ||
                  Math.abs(currentPage - page) <= 1
                )
                .reduce((acc, page, idx, arr) => {
                  if (idx > 0 && page - arr[idx - 1] > 1) {
                    acc.push("ellipsis");
                  }
                  acc.push(page);
                  return acc;
                }, [] as (number | "ellipsis")[])
                .map((item, index) =>
                  item === "ellipsis" ? (
                    <span key={`dots-${index}`} className="w-8 text-center">...</span>
                  ) : (
                    <button
                      key={item}
                      onClick={() => setCurrentPage(item)}
                      className={`w-8 h-8 rounded-md border text-sm ${currentPage === item
                        ? "bg-yellow-400 text-white font-bold"
                        : "text-gray-600 hover:bg-gray-100"
                        }`}
                    >
                      {String(item).padStart(2, "0")}
                    </button>
                  )
                )}

              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="w-8 h-8 rounded-md border hover:bg-gray-100 text-sm disabled:opacity-50"
              >
                →
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Blogs;
